<template>
  <div class="iphone-container">
    <!-- iPhone Frame -->
    <div class="iphone-frame">
      <!-- Dynamic Notch -->
      <div class="iphone-notch">
        <div class="camera"></div>
        <div class="sensor"></div>
        <div class="speaker"></div>
      </div>
      <!-- Screen Content -->
      <div class="screen">
        <div class="content-container">
          <!-- Detail Image -->
          <div class="image-container">
            <div
                v-if="currentDetailViewImage"
                :style="{ backgroundImage: 'url(' + currentDetailViewImage + ')' }"
                class="detail-image"
            ></div>
            <div v-else class="detail-image-placeholder"></div>
          </div>

          <!-- Content Section -->
          <div class="content-section">
            <!-- Headline -->
            <h1 v-if="currentHeadline" class="headline">{{ stripHtmlTags(currentHeadline) }}</h1>
            <!-- Tag-Chips -->
            <div v-if="currentTagChips && currentTagChips.length > 0" class="chips-preview">
              <div v-for="(chip, i) in currentTagChips" :key="`chip-${i}`" class="chip-row">
                <template v-if="chip && chip.name && chip.value">
                  <span class="chip-name">{{ chip.name }}</span> &nbsp;{{ chip.value }}
                </template>
              </div>
            </div>
            <!-- Body -->
            <div v-if="currentBody" class="body">{{ stripHtmlTags(currentBody) }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "LoyaltyPointScanDetailViewMobilePreview",
  props: {
    initialDetailViewImage: String,
    initialHeadline: String,
    initialBody: String,
    initialTagChips: {
      type: Array,
      default: () => []
    },
  },
  data() {
    return {
      eventDetailViewImage: null,
      eventHeadline: '',
      eventBody: '',
      eventTagChips: []
    };
  },
  computed: {
    currentDetailViewImage() {
      return this.eventDetailViewImage !== null ? this.eventDetailViewImage : this.initialDetailViewImage;
    },
    currentHeadline() {
      return this.eventHeadline !== '' ? this.eventHeadline : (this.initialHeadline || '');
    },
    currentBody() {
      return this.eventBody !== '' ? this.eventBody : (this.initialBody || '');
    },
    currentTagChips() {
      let raw = Array.isArray(this.eventTagChips) && this.eventTagChips.length
          ? this.eventTagChips
          : (typeof this.initialTagChips === 'string'
              ? (() => {
                try {
                  return JSON.parse(this.initialTagChips)
                } catch {
                  return []
                }
              })()
              : (Array.isArray(this.initialTagChips)
                  ? this.initialTagChips
                  : []))

      return raw.filter(chip =>
          chip && chip.name && chip.value &&
          chip.name.trim() !== '' && chip.value.trim() !== ''
      )
    }
  },
  methods: {
    stripHtmlTags(html) {
      if (!html) return '';
      const div = document.createElement('div');
      div.innerHTML = html;
      return div.textContent || div.innerText || '';
    },
    validateTagChips(chips) {
      if (!Array.isArray(chips)) {
        return [];
      }

      return chips.filter(chip =>
          chip &&
          typeof chip === 'object' &&
          chip.name &&
          chip.value &&
          chip.name.trim() !== '' &&
          chip.value.trim() !== ''
      );
    }
  },
  mounted() {
    window.addEventListener("detail-view-image-selected", (event) => {
      this.eventDetailViewImage = event.detail;
    });
    window.addEventListener("headline-updated", (event) => {
      this.eventHeadline = event.detail;
    });
    window.addEventListener("body-updated", (event) => {
      this.eventBody = event.detail;
    });
    window.addEventListener('tag-chips-updated', event => {
      this.eventTagChips = this.validateTagChips(event.detail);
    });
  },
};
</script>

<style scoped>
/* Container for centering the iPhone preview */
.iphone-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* iPhone Frame */
.iphone-frame {
  width: 390px; /* iPhone width */
  height: 844px; /* iPhone height */
  background: #fff;
  border-radius: 40px;
  border: 8px solid #222;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

/* Notch (Dynamic Island style) */
.iphone-notch {
  width: 140px;
  height: 35px;
  background: #222;
  border-radius: 20px;
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 5px;
  z-index: 10;
}

/* Notch elements */
.camera {
  width: 12px;
  height: 12px;
  background: black;
  border-radius: 50%;
}

.sensor {
  width: 8px;
  height: 8px;
  background: grey;
  border-radius: 50%;
}

.speaker {
  width: 40px;
  height: 5px;
  background: darkgray;
  border-radius: 3px;
}

/* Screen area */
.screen {
  flex-grow: 1;
  background: white;
  margin-top: 50px; /* Space for notch */
  position: relative;
  overflow-y: auto;
}

/* Content container */
.content-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Image container */
.image-container {
  flex-shrink: 0;
}

/* Detail Image */
.detail-image {
  width: 100%;
  height: 200px;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
}

/* Grey placeholder if no image is selected */
.detail-image-placeholder {
  width: 100%;
  height: 200px;
  background-color: #efefef;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 14px;
}

.detail-image-placeholder::before {
  content: "No Image";
}

/* Content section */
.content-section {
  flex-grow: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

/* Headline styling */
.headline {
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin: 0;
  line-height: 1.2;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

/* Body styling */
.body {
  font-size: 16px;
  font-weight: normal;
  color: #444;
  line-height: 1.5;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: pre-wrap; /* Preserves newlines and spaces */
  flex-grow: 1;
}

.chips-preview {
  margin: 10px 0;
}

.chip-row {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;
  margin-bottom: 4px;
}

.chip-name {
  background: #dcdcdc;
  color: #494949;
  border-radius: 14px;
  padding: 5px 8px 5px 8px;
  font-size: 13px;
}
</style>
